import React, { forwardRef, useImperativeHandle } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';

import { InteractiveForwardRefProps } from '@/components/interactive';

const SIZE = 100.0;
const CIRCLE_RADIUS = SIZE * 2;

export const PanGestureDemo = forwardRef<InteractiveForwardRefProps>(
  (_, ref) => {
    const translateX = useSharedValue(0);
    const translateY = useSharedValue(0);
    const startX = useSharedValue(0);
    const startY = useSharedValue(0);

    const panGesture = Gesture.Pan()
      .onBegin(() => {
        startX.value = translateX.value;
        startY.value = translateY.value;
      })
      .onUpdate((event) => {
        translateX.value = event.translationX + startX.value;
        translateY.value = event.translationY + startY.value;
      })
      .onEnd(() => {
        const distance = Math.sqrt(
          translateX.value ** 2 + translateY.value ** 2
        );

        if (distance < CIRCLE_RADIUS + SIZE / 2) {
          translateX.value = withSpring(0);
          translateY.value = withSpring(0);
        }
      });

    const rStyle = useAnimatedStyle(() => {
      return {
        transform: [
          {
            translateX: translateX.value,
          },
          {
            translateY: translateY.value,
          },
        ],
      };
    });

    const onReset = () => {
      translateX.value = withSpring(0);
      translateY.value = withSpring(0);
    };

    const onPlay = () => {
      // Simulate a drag by animating to a random position
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * (CIRCLE_RADIUS - SIZE / 2);
      translateX.value = withSpring(Math.cos(angle) * distance);
      translateY.value = withSpring(Math.sin(angle) * distance);
    };

    useImperativeHandle(ref, () => ({
      play: onPlay,
      reset: onReset,
      pause: () => {}, // No pause functionality for this component
    }));

    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <View style={styles.container}>
          <View style={styles.circle}>
            <GestureDetector gesture={panGesture}>
              <Animated.View style={[styles.square, rStyle]} />
            </GestureDetector>
          </View>
        </View>
      </GestureHandlerRootView>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  square: {
    width: SIZE,
    height: SIZE,
    backgroundColor: 'rgba(0, 0, 255, 0.5)',
    borderRadius: 20,
  },
  circle: {
    width: CIRCLE_RADIUS * 2,
    height: CIRCLE_RADIUS * 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: CIRCLE_RADIUS,
    borderWidth: 5,
    borderColor: 'rgba(0, 0, 255, 0.5)',
  },
});
